/* Fig & Bloom — scope the "You might also like" rail to the shopper's
   delivery location.

   Shopify's recommendations endpoint knows nothing about fulfilment
   territories, so the rail happily suggested products the shopper's city
   cannot receive. sections/product-recommendations.liquid now over-fetches
   (limit=10, the endpoint's maximum), renders every candidate with the same
   product-level availability roll-up the suburb grids use
   (snippets/fb-location-availability-attrs.liquid), and this file removes
   the cards the shopper cannot buy, then trims what is left to the
   section's products_to_show (data-show-count). Sold-out products never
   reach the page at all — the section skips them in Liquid, because stock
   is server-known and territory is not.

   WHY THIS IS JAVASCRIPT
   Liquid cannot resolve a shopper to a fulfilment location: the postcode
   tables are JS-only, the delivery suburb is a cookie rather than a cart
   attribute, and the endpoint response is CDN-cached. Same constraint,
   same answer as fb-suburb-grid.js, fb-variant-location-filter.js and
   cart-drawer-addons.js.

   FAIL OPEN, ALWAYS
   No cookie, no resolver on the page, an unresolvable postcode, or a card
   with no flag for this location all leave the rail exactly as the server
   ranked it (minus the display cap, which is not a location decision).
   This is a discovery surface, not an add-to-cart gate — the gate itself
   is already fail-closed.

   THE HIDE RULE MIRRORS THE ADD-TO-CART GATE (assets/product-form.js:773-777)
       managed = the product declares `true` for at least one location
       remove when: managed && a flag exists for THIS location && it is not 'true'
   A product with NO flag for this location stays visible — the client's
   decision of 1 Aug 2026 ("missing means unavailable" was built and
   reverted; it would have hidden 152 products in Adelaide alone). Looser
   than the gate would recommend a product the PDP then refuses to sell;
   stricter would hide sellable stock. Do not change one without the other.

   CARDS ARE REMOVED, NOT HIDDEN
   The carousel is Swiper with loop:true, which clones every child of the
   wrapper — a display:none slide would still be cloned and paged to.
   global.js therefore calls scope() after the fetched slides land and
   BEFORE the Swiper is built.

   RE-SCOPING ON SUBURB CHANGE
   `deliveryInfoUpdated` (fired on window by the delivery popup, the same
   signal cart-drawer-addons.js listens for) triggers a full reload of each
   loaded rail via el.loadRecommendations() — fetch, scope, Swiper rebuild —
   because removed cards cannot be restored into a live looped Swiper. The
   event fires only on an explicit suburb choice (and the event-cart clear),
   so the extra GET is rare and the response is typically browser-cached. */
(function () {
  'use strict';

  var POSTCODE_COOKIE = 'deliveryPostcode';

  /* Same five as the rest of the codebase (fb-suburb-grid.js,
     cart-drawer-addons.js, delivery-suburb-popup.js). Only a fallback:
     window.fbLocationId is authoritative and covers a new city with no code
     change here. */
  var STATIC_LOCATION_IDS = {
    Brisbane: '70541672681',
    Melbourne: '70507725033',
    Sydney: '70541639913',
    RestofAustralia: '74290069737',
    Adelaide: '90760315113'
  };

  function getCookie(name) {
    var parts = ('; ' + document.cookie).split('; ' + name + '=');
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  }

  /* window.fbResolvePostcode has TWO failure modes and only one is falsy:
       null               -> no app config on the page at all
       'Unknown Location' -> a complete config that matched nothing
     The string is truthy; treating it as a location cost a real bug in the
     cart extension on 1 Aug 2026. Both mean "we cannot place this shopper"
     -> fail open. window.findPostcodeLocation is deliberately NOT a
     fallback: with its postcode arrays missing it returns a hardcoded
     "Sydney" (delivery-suburb-popup.js:1394), and guessing a city would
     hide products for no reason. */
  function resolveLocationId() {
    var postcode = getCookie(POSTCODE_COOKIE);
    if (!postcode) return null;
    if (typeof window.fbResolvePostcode !== 'function') return null;

    var location = window.fbResolvePostcode(postcode);
    if (!location || location === 'Unknown Location') return null;

    var id = null;
    if (typeof window.fbLocationId === 'function') id = window.fbLocationId(location);
    id = id || STATIC_LOCATION_IDS[location] || null;
    return id ? String(id) : null;
  }

  function isBlocked(card, locationId) {
    if (!locationId) return false;
    if (card.getAttribute('data-loc-managed') !== 'true') return false;
    var flag = card.getAttribute('data-avail-' + locationId);
    if (flag === null) return false;
    return flag !== 'true';
  }

  /* Remove undeliverable cards, then trim the survivors to data-show-count.
     Card order is the endpoint's relevance ranking, so trimming from the
     back keeps the strongest recommendations — the over-fetch exists purely
     so removals backfill from ranks 9-10 instead of shortening the rail. */
  function scope(root) {
    var result = { status: 'scoped', locationId: null, total: 0, shown: 0, blocked: 0 };
    var wrapper = root.querySelector('[grid-recommendation]');
    if (!wrapper) {
      result.status = 'no-grid';
      window.fbRecsScope = result;
      return result;
    }

    /* Snapshot before removing — a live children collection reindexes under
       removeChild and skips every element after a removed one. */
    var cards = [];
    var i;
    for (i = 0; i < wrapper.children.length; i++) {
      if (wrapper.children[i].hasAttribute('data-fb-rec-card')) cards.push(wrapper.children[i]);
    }
    result.total = cards.length;

    var locationId = resolveLocationId();
    result.locationId = locationId;
    if (!locationId) result.status = 'unresolved';

    var showCount = parseInt(root.getAttribute('data-show-count'), 10) || cards.length;

    for (i = 0; i < cards.length; i++) {
      if (isBlocked(cards[i], locationId)) {
        wrapper.removeChild(cards[i]);
        result.blocked++;
      } else if (result.shown < showCount) {
        result.shown++;
      } else {
        wrapper.removeChild(cards[i]);
      }
    }

    /* Nothing deliverable survived: hide the whole section rather than
       render a heading over an empty carousel frame. Same class the
       zero-recommendations path in global.js uses, toggled so a later
       re-scope to a served territory brings the section back. */
    if (root.parentElement) {
      root.parentElement.classList.toggle('no-js-inline', result.shown === 0);
    }

    /* Diagnostic, mirroring window.fbSuburbGrid — read by the test and when
       verifying a page in the browser. */
    window.fbRecsScope = result;
    return result;
  }

  window.fbRecsLocationFilter = { scope: scope };

  window.addEventListener('deliveryInfoUpdated', function () {
    var rails = document.querySelectorAll('product-recommendations[data-intent="related"]');
    for (var i = 0; i < rails.length; i++) {
      /* Only rails that have actually fetched: an unloaded rail's
         IntersectionObserver will load it with the fresh cookie anyway, and
         reloading it here would defeat the lazy-load. */
      if (
        rails[i].classList.contains('product-recommendations--loaded') &&
        typeof rails[i].loadRecommendations === 'function'
      ) {
        rails[i].loadRecommendations();
      }
    }
  });
})();
